import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export type PdfLanguage = "th" | "en";

export interface HistoryPdfSession {
  date: string;
  staffName: string;
  platformName: string;
  startTime: string;
  endTime: string | null;
  durationMins: number;
  ot: number;
  verenaSales: number;
  affiliateSales: number;
  commission: number;
  totalRevenue: number;
  roi: number;
  startNote: string;
  endNote: string;
  status: "ACTIVE" | "COMPLETED";
}

export interface HistoryPdfSettings {
  partTimeRate: number;
  fullTimeRate: number;
  commRate: number;
  roiGood: number;
  roiOk: number;
}

interface PdfInput {
  language: PdfLanguage;
  month: string;
  sessions: HistoryPdfSession[];
  settings: HistoryPdfSettings;
}

interface StaffAggregate {
  name: string;
  sessions: number;
  verena: number;
  commission: number;
  ot: number;
  revenue: number;
  roi: number;
}

interface DailyAggregate {
  verena: number;
  commission: number;
  ot: number;
  revenue: number;
}

interface ChartBar {
  label: string;
  value: number;
  color: string;
}

type Rgb = [number, number, number];

let regularFont: string | undefined;
let boldFont: string | undefined;

const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const ENGLISH_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const palette = ["#ec4899", "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#06b6d4"];

function money(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function parsedMonth(value: string): { year: number; monthIndex: number } {
  const [yearValue, monthValue] = value.split("-");
  const year = Number(yearValue);
  const monthIndex = Number(monthValue) - 1;
  return { year: Number.isFinite(year) ? year : new Date().getFullYear(), monthIndex: Math.max(0, Math.min(11, monthIndex)) };
}

function reportPeriod(month: string, language: PdfLanguage): { title: string; fileMonth: string } {
  const { year, monthIndex } = parsedMonth(month);
  return language === "th"
    ? { title: `${THAI_MONTHS[monthIndex]} ${year + 543}`, fileMonth: ENGLISH_MONTHS[monthIndex] }
    : { title: `${ENGLISH_MONTHS[monthIndex]} ${year}`, fileMonth: ENGLISH_MONTHS[monthIndex] };
}

function localTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Bangkok",
  }).format(new Date(value));
}

function duration(minutes: number): string {
  if (!minutes) return "—";
  const hours = Math.floor(minutes / 60);
  const remainder = Math.round(minutes % 60);
  return hours ? `${hours}h ${remainder}m` : `${remainder}m`;
}

function fontBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let result = "";
  const chunkSize = 8192;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    result += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length)));
  }
  return btoa(result);
}

async function useSarabun(doc: jsPDF): Promise<string> {
  try {
    if (!regularFont || !boldFont) {
      const base = "https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/sarabun/";
      const [regular, bold] = await Promise.all([
        fetch(`${base}Sarabun-Regular.ttf`),
        fetch(`${base}Sarabun-Bold.ttf`),
      ]);
      if (!regular.ok || !bold.ok) throw new Error("Unable to load Sarabun font");
      [regularFont, boldFont] = await Promise.all([
        regular.arrayBuffer().then(fontBase64),
        bold.arrayBuffer().then(fontBase64),
      ]);
    }
    doc.addFileToVFS("Sarabun-Regular.ttf", regularFont);
    doc.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    doc.addFileToVFS("Sarabun-Bold.ttf", boldFont);
    doc.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    return "Sarabun";
  } catch {
    return "helvetica";
  }
}

function chartCanvas(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable");
  return context;
}

function canvasImage(context: CanvasRenderingContext2D): string {
  return context.canvas.toDataURL("image/png");
}

function drawBarChart(bars: ChartBar[], width = 900, height = 460, suffix = ""): string {
  const context = chartCanvas(width, height);
  const left = 58;
  const right = 22;
  const top = 35;
  const bottom = 66;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const maximum = Math.max(...bars.map((bar) => bar.value), 1);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.font = "18px Arial";
  context.fillStyle = "#6b7280";
  context.textAlign = "right";
  for (let step = 0; step <= 4; step += 1) {
    const y = top + chartHeight - (step / 4) * chartHeight;
    context.strokeStyle = "#e5e7eb";
    context.beginPath();
    context.moveTo(left, y);
    context.lineTo(width - right, y);
    context.stroke();
    context.fillText(`${Math.round((maximum * step) / 4)}${suffix}`, left - 8, y + 6);
  }
  const slot = chartWidth / Math.max(bars.length, 1);
  bars.forEach((bar, index) => {
    const barWidth = Math.max(18, slot * 0.62);
    const barHeight = (bar.value / maximum) * chartHeight;
    const x = left + index * slot + (slot - barWidth) / 2;
    const y = top + chartHeight - barHeight;
    context.fillStyle = bar.color;
    context.fillRect(x, y, barWidth, barHeight);
    context.textAlign = "center";
    context.fillStyle = "#374151";
    context.font = "bold 17px Arial";
    context.fillText(`${money(bar.value)}${suffix}`, x + barWidth / 2, Math.max(23, y - 8));
    context.font = "16px Arial";
    const label = bar.label.length > 13 ? `${bar.label.slice(0, 12)}…` : bar.label;
    context.fillText(label, x + barWidth / 2, height - 27);
  });
  return canvasImage(context);
}

function drawDailyChart(rows: Array<{ day: string; daily: DailyAggregate }>): string {
  const width = 1100;
  const height = 520;
  const context = chartCanvas(width, height);
  const left = 62;
  const right = 60;
  const top = 42;
  const bottom = 72;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const maxSales = Math.max(...rows.map((row) => Math.max(row.daily.verena, row.daily.commission)), 1);
  const maxRoi = Math.max(...rows.map((row) => row.daily.revenue / (row.daily.ot || 1)), 1);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.font = "18px Arial";
  for (let tick = 0; tick <= 4; tick += 1) {
    const y = top + chartHeight - (tick / 4) * chartHeight;
    context.strokeStyle = "#e5e7eb";
    context.beginPath(); context.moveTo(left, y); context.lineTo(width - right, y); context.stroke();
    context.fillStyle = "#6b7280"; context.textAlign = "right";
    context.fillText(money((maxSales * tick) / 4), left - 8, y + 6);
    context.textAlign = "left";
    context.fillText(`${((maxRoi * tick) / 4).toFixed(1)}x`, width - right + 8, y + 6);
  }
  const slot = chartWidth / Math.max(rows.length, 1);
  const points: Array<{ x: number; y: number }> = [];
  rows.forEach((row, index) => {
    const baseX = left + index * slot;
    const verenaWidth = Math.max(4, slot * 0.28);
    const commissionWidth = Math.max(3, slot * 0.18);
    const verenaHeight = (row.daily.verena / maxSales) * chartHeight;
    const commissionHeight = (row.daily.commission / maxSales) * chartHeight;
    context.fillStyle = "#388e3c";
    context.fillRect(baseX + slot * 0.12, top + chartHeight - verenaHeight, verenaWidth, verenaHeight);
    context.fillStyle = "#42a5f5";
    context.fillRect(baseX + slot * 0.47, top + chartHeight - commissionHeight, commissionWidth, commissionHeight);
    points.push({ x: baseX + slot * 0.55, y: top + chartHeight - ((row.daily.revenue / (row.daily.ot || 1)) / maxRoi) * chartHeight });
    context.font = "16px Arial"; context.fillStyle = "#4b5563"; context.textAlign = "center";
    context.fillText(String(Number(row.day)), baseX + slot / 2, height - 30);
  });
  context.strokeStyle = "#f59e0b";
  context.lineWidth = 4;
  context.beginPath();
  points.forEach((point, index) => index === 0 ? context.moveTo(point.x, point.y) : context.lineTo(point.x, point.y));
  context.stroke();
  points.forEach((point) => { context.fillStyle = "#ffffff"; context.beginPath(); context.arc(point.x, point.y, 5, 0, Math.PI * 2); context.fill(); context.strokeStyle = "#f59e0b"; context.lineWidth = 3; context.stroke(); });
  context.textAlign = "left"; context.font = "bold 18px Arial";
  context.fillStyle = "#388e3c"; context.fillText("■ Verena Sales", left, 20);
  context.fillStyle = "#42a5f5"; context.fillText("■ Commission", left + 180, 20);
  context.fillStyle = "#f59e0b"; context.fillText("● ROI", left + 330, 20);
  return canvasImage(context);
}

function drawDonut(before: number, after: number, beforeLabel: string, afterLabel: string): string {
  const context = chartCanvas(600, 550);
  const total = before + after;
  const centerX = 225;
  const centerY = 255;
  const radius = 150;
  let cursor = -Math.PI / 2;
  const slices = [
    { value: before, color: "#10b981", label: beforeLabel },
    { value: after, color: "#ec4899", label: afterLabel },
  ];
  context.fillStyle = "#ffffff"; context.fillRect(0, 0, 600, 550);
  slices.forEach((slice) => {
    const angle = total ? (slice.value / total) * Math.PI * 2 : Math.PI;
    context.fillStyle = slice.color;
    context.beginPath(); context.moveTo(centerX, centerY); context.arc(centerX, centerY, radius, cursor, cursor + angle); context.closePath(); context.fill();
    cursor += angle;
  });
  context.fillStyle = "#ffffff"; context.beginPath(); context.arc(centerX, centerY, 82, 0, Math.PI * 2); context.fill();
  context.fillStyle = "#374151"; context.font = "bold 26px Arial"; context.textAlign = "center"; context.fillText("Sales", centerX, centerY - 6); context.font = "22px Arial"; context.fillText(money(total), centerX, centerY + 25);
  context.textAlign = "left"; context.font = "20px Arial";
  slices.forEach((slice, index) => { const y = 445 + index * 34; context.fillStyle = slice.color; context.fillRect(72, y - 15, 18, 18); context.fillStyle = "#374151"; const pct = total ? ((slice.value / total) * 100).toFixed(1) : "0.0"; context.fillText(`${slice.label}: ${pct}%`, 102, y); });
  return canvasImage(context);
}

function header(doc: jsPDF, font: string, title: string, period: string): void {
  doc.setFillColor(231, 111, 81); doc.rect(0, 0, 210, 10, "F");
  doc.setFont(font, "bold"); doc.setFontSize(11); doc.setTextColor(255, 255, 255); doc.text(title, 14, 7.5);
  doc.setFont(font, "normal"); doc.setFontSize(8); doc.text(period, 196, 7.5, { align: "right" });
}

function footer(doc: jsPDF, font: string, settings: HistoryPdfSettings, language: PdfLanguage): void {
  const pages = doc.getNumberOfPages();
  for (let index = 1; index <= pages; index += 1) {
    doc.setPage(index);
    doc.setFillColor(248, 244, 235); doc.rect(0, 289, 210, 8, "F");
    doc.setFont(font, "normal"); doc.setFontSize(6.5); doc.setTextColor(139, 120, 102);
    doc.text(`${language === "th" ? "พาร์ตไทม์" : "Part-time"} = ${money(settings.partTimeRate)} ${language === "th" ? "บาท/ชม" : "THB/hr"} · ${language === "th" ? "พนักงานประจำ" : "Full-time"} = ${money(settings.fullTimeRate)} ${language === "th" ? "บาท/ชม" : "THB/hr"} · Commission = ${settings.commRate}% · ©2026 Verena International`, 14, 294.5);
    doc.text(`Page ${index} / ${pages}`, 196, 294.5, { align: "right" });
  }
}

export async function downloadHistoryPdf(input: PdfInput): Promise<void> {
  const sessions = input.sessions.filter((session) => session.status === "COMPLETED");
  if (!sessions.length) throw new Error("No completed sessions to export");
  const isThai = input.language === "th";
  const period = reportPeriod(input.month, input.language);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const font = await useSarabun(doc);
  const total = (key: keyof Pick<HistoryPdfSession, "verenaSales" | "affiliateSales" | "commission" | "totalRevenue" | "ot" | "durationMins">): number => sessions.reduce((sum, session) => sum + session[key], 0);
  const verena = total("verenaSales");
  const affiliate = total("affiliateSales");
  const commission = total("commission");
  const revenue = total("totalRevenue");
  const ot = total("ot");
  const minutes = total("durationMins");
  const roi = ot > 0 ? revenue / ot : 0;
  const staffMap = new Map<string, Omit<StaffAggregate, "name" | "roi">>();
  sessions.forEach((session) => {
    const current = staffMap.get(session.staffName) ?? { sessions: 0, verena: 0, commission: 0, ot: 0, revenue: 0 };
    current.sessions += 1; current.verena += session.verenaSales; current.commission += session.commission; current.ot += session.ot; current.revenue += session.totalRevenue;
    staffMap.set(session.staffName, current);
  });
  const staff: StaffAggregate[] = [...staffMap.entries()].map(([name, values]) => ({ ...values, name, roi: values.ot ? values.revenue / values.ot : 0 })).sort((left, right) => right.revenue - left.revenue);
  const dailyMap = new Map<string, DailyAggregate>();
  sessions.forEach((session) => {
    const day = session.date.slice(-2);
    const current = dailyMap.get(day) ?? { verena: 0, commission: 0, ot: 0, revenue: 0 };
    current.verena += session.verenaSales; current.commission += session.commission; current.ot += session.ot; current.revenue += session.totalRevenue;
    dailyMap.set(day, current);
  });
  const daily = [...dailyMap.entries()].map(([day, values]) => ({ day, daily: values })).sort((left, right) => Number(left.day) - Number(right.day));
  const before22 = sessions.filter((session) => Number(localTime(session.startTime).slice(0, 2)) < 22);
  const after22 = sessions.filter((session) => Number(localTime(session.startTime).slice(0, 2)) >= 22);
  const grouped = (rows: HistoryPdfSession[]) => ({ revenue: rows.reduce((sum, row) => sum + row.totalRevenue, 0), ot: rows.reduce((sum, row) => sum + row.ot, 0) });
  const early = grouped(before22); const late = grouped(after22);
  const earlyRoi = early.ot ? early.revenue / early.ot : 0; const lateRoi = late.ot ? late.revenue / late.ot : 0;
  const beforeLabel = isThai ? "ก่อน 22:00 น." : "Before 22:00";
  const afterLabel = isThai ? "22:00 น. ขึ้นไป" : "After 22:00";
  const issueCount = sessions.filter((session) => session.startNote || session.endNote).length;
  const margin = 14;

  doc.setFillColor(231, 111, 81); doc.rect(0, 0, 210, 36, "F"); doc.setFillColor(196, 94, 8); doc.rect(0, 30, 210, 6, "F");
  doc.setFont(font, "bold"); doc.setTextColor(255, 255, 255); doc.setFontSize(20); doc.text(isThai ? "รายงาน ROI การไลฟ์สด" : "Live Streaming ROI Report", margin, 14);
  doc.setFont(font, "normal"); doc.setFontSize(10); doc.text(`Verena International  ·  ${period.title}`, margin, 21);
  doc.setFontSize(8); doc.text(`${isThai ? "พาร์ตไทม์" : "Part-time"} ${money(input.settings.partTimeRate)} ${isThai ? "บาท/ชม" : "THB/hr"} · ${isThai ? "พนักงานประจำ" : "Full-time"} ${money(input.settings.fullTimeRate)} ${isThai ? "บาท/ชม" : "THB/hr"} · Commission ${input.settings.commRate}%`, margin, 27);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")}`, 196, 27, { align: "right" });
  let y = 46;
  doc.setTextColor(44, 27, 17); doc.setFont(font, "bold"); doc.setFontSize(13); doc.text(isThai ? "สรุปผู้บริหาร" : "Executive Summary", margin, y);
  doc.setDrawColor(231, 111, 81); doc.setLineWidth(0.6); doc.line(margin, y + 1.5, 196, y + 1.5);
  y += 7; doc.setFont(font, "normal"); doc.setFontSize(9.5); doc.setTextColor(70, 55, 42);
  const executive = isThai
    ? `ในเดือน${period.title} ทีมไลฟ์สดดำเนินการ ${sessions.length} sessions รวม ${(minutes / 60).toFixed(1)} ชั่วโมง พนักงาน ${staff.length} คน ยอดขาย Verena ${money(verena)} บาท + Commission ${money(commission)} บาท = รายได้สุทธิ ${money(revenue)} บาท ค่าแรง ${money(ot)} บาท · ROI รวม ${roi.toFixed(2)}x (${roi >= input.settings.roiGood ? "ดีเยี่ยม" : roi >= input.settings.roiOk ? "พอใช้" : "ต่ำกว่าเป้า"}) ${issueCount ? `มีรายงานปัญหา ${issueCount} รายการ` : "ไม่มีรายงานปัญหา"}`
    : `In ${period.title}, ${sessions.length} sessions (${(minutes / 60).toFixed(1)} hrs, ${staff.length} streamers). Verena THB ${money(verena)} + Commission THB ${money(commission)} = Net Revenue THB ${money(revenue)}. Labor cost: THB ${money(ot)} · ROI: ${roi.toFixed(2)}x (${roi >= input.settings.roiGood ? "EXCELLENT" : roi >= input.settings.roiOk ? "ACCEPTABLE" : "BELOW TARGET"}). ${issueCount ? `${issueCount} issue(s) reported.` : "No issues reported."}`;
  const executiveLines = doc.splitTextToSize(executive, 182);
  doc.text(executiveLines, margin, y); y += executiveLines.length * 4.5 + 4;
  const cards: Array<[string, string, Rgb, Rgb]> = [
    [isThai ? "ยอดขาย VERENA" : "VERENA SALES", money(verena), [219, 234, 254], [30, 64, 175]],
    [isThai ? "ยอดขาย AFFILIATE" : "AFFILIATE SALES", money(affiliate), [237, 233, 254], [91, 33, 182]],
    [`COMMISSION ${input.settings.commRate}%`, money(commission), [254, 243, 199], [146, 64, 14]],
    [isThai ? "รายได้สุทธิ" : "NET REVENUE", money(revenue), [209, 250, 229], [6, 95, 70]],
    [isThai ? "ค่าแรงรวม" : "TOTAL LABOR COST", money(ot), [254, 226, 226], [153, 27, 27]],
    [isThai ? "ROI รวม" : "OVERALL ROI", `${roi.toFixed(2)}x`, roi >= input.settings.roiGood ? [209, 250, 229] : roi >= input.settings.roiOk ? [254, 243, 199] : [254, 226, 226], roi >= input.settings.roiGood ? [6, 95, 70] : roi >= input.settings.roiOk ? [146, 64, 14] : [153, 27, 27]],
    ["SESSIONS", String(sessions.length), [252, 231, 243], [157, 23, 77]],
    [isThai ? "ชั่วโมงรวม" : "TOTAL HOURS", `${(minutes / 60).toFixed(1)}h`, [207, 250, 254], [21, 94, 117]],
  ];
  const cardWidth = 40.75; const cardHeight = 17;
  cards.forEach(([label, value, background, foreground], index) => { const x = margin + (index % 4) * (cardWidth + 3); const cardY = y + Math.floor(index / 4) * (cardHeight + 3); doc.setFillColor(...background); doc.roundedRect(x, cardY, cardWidth, cardHeight, 2.5, 2.5, "F"); doc.setFillColor(...foreground); doc.rect(x, cardY, cardWidth, 1, "F"); doc.setTextColor(...foreground); doc.setFont(font, "normal"); doc.setFontSize(6.5); doc.text(label, x + 3, cardY + 6); doc.setFont(font, "bold"); doc.setFontSize(11); doc.text(value, x + 3, cardY + 14); });
  y += 44;
  const best = [...sessions].sort((left, right) => right.totalRevenue - left.totalRevenue)[0];
  const insights: Array<[string, string, Rgb, Rgb]> = [
    ["BEST SESSION", isThai ? `${best.date} · ${best.staffName} (${best.platformName || "—"}) — ${money(best.totalRevenue)} บาท · ROI ${best.roi.toFixed(2)}x` : `${best.date} · ${best.staffName} (${best.platformName || "—"}) — THB ${money(best.totalRevenue)} · ROI ${best.roi.toFixed(2)}x`, [209, 250, 229], [6, 95, 70]],
    ["TOP PERFORMER", isThai ? `${staff[0].name}: ${staff[0].sessions} sessions · ${money(staff[0].revenue)} บาท · ROI ${staff[0].roi.toFixed(2)}x` : `${staff[0].name}: ${staff[0].sessions} sessions · THB ${money(staff[0].revenue)} · ROI ${staff[0].roi.toFixed(2)}x`, [219, 234, 254], [30, 64, 175]],
    ["TIME SLOT", isThai ? `${beforeLabel} ROI = ${earlyRoi.toFixed(2)}x (${revenue ? ((early.revenue / revenue) * 100).toFixed(1) : "0"}% ของยอด) | ${afterLabel} ROI = ${lateRoi.toFixed(2)}x (${revenue ? ((late.revenue / revenue) * 100).toFixed(1) : "0"}%)` : `${beforeLabel} ROI = ${earlyRoi.toFixed(2)}x (${revenue ? ((early.revenue / revenue) * 100).toFixed(1) : "0"}% of total) | ${afterLabel} ROI = ${lateRoi.toFixed(2)}x (${revenue ? ((late.revenue / revenue) * 100).toFixed(1) : "0"}%)`, [254, 243, 199], [146, 64, 14]],
  ];
  insights.forEach(([tag, text, background, foreground]) => { doc.setFillColor(...background); doc.roundedRect(margin, y, 182, 9.5, 2, 2, "F"); doc.setFillColor(...foreground); doc.roundedRect(margin, y, 26, 9.5, 2, 2, "F"); doc.setFont(font, "bold"); doc.setTextColor(255, 255, 255); doc.setFontSize(6.5); doc.text(tag, margin + 13, y + 6.2, { align: "center" }); doc.setFont(font, "normal"); doc.setTextColor(40, 40, 40); doc.setFontSize(8); doc.text(doc.splitTextToSize(text, 152)[0], margin + 29, y + 6.2); y += 11; });

  doc.addPage(); header(doc, font, isThai ? "ยอดขายและ ROI รายวัน" : "Daily Sales & ROI", period.title);
  doc.addImage(drawDailyChart(daily), "PNG", margin, 14, 182, 78);
  doc.setFont(font, "normal"); doc.setFontSize(7.5); doc.setTextColor(100, 80, 60); doc.text(isThai ? "แกนซ้าย = ยอดขาย (บาท)  |  แกนขวา = ROI (x)  |  แกน X = วันที่" : "Left axis = Sales (THB)  |  Right axis = ROI (x)  |  X = Day of month", 105, 95, { align: "center" });
  const topDays = daily.map((entry) => ({ ...entry, roi: entry.daily.ot ? entry.daily.revenue / entry.daily.ot : 0 })).sort((left, right) => right.roi - left.roi).slice(0, 3);
  let topY = 102;
  topDays.forEach((entry, index) => { const x = index < 2 ? margin + index * 93 : margin; const width = index < 2 ? 89 : 182; const staffOnDay = [...new Set(sessions.filter((session) => session.date.slice(-2) === entry.day).map((session) => session.staffName))].join(", "); const label = isThai ? `วันที่ ${Number(entry.day)} (${staffOnDay}): ROI ${entry.roi.toFixed(2)}x · ยอดขาย ${money(entry.daily.verena)} บาท` : `Day ${Number(entry.day)} (${staffOnDay}): ROI ${entry.roi.toFixed(2)}x · Sales THB ${money(entry.daily.verena)}`; const lines = doc.splitTextToSize(label, width - 8); const height = lines.length * 4 + 5; doc.setFillColor(209, 250, 229); doc.roundedRect(x, topY, width, height, 2, 2, "F"); doc.setFillColor(6, 95, 70); doc.circle(x + 3.5, topY + height / 2, 1.5, "F"); doc.setTextColor(30, 70, 40); doc.setFontSize(8); doc.text(lines, x + 7, topY + 4.5); if (index === 1) topY += height + 2; if (index === 2) topY += height + 2; });

  doc.addPage(); header(doc, font, isThai ? "ประสิทธิภาพแยกตามพนักงาน" : "Performance by Streamer", period.title);
  const half = 89;
  doc.setFillColor(56, 142, 60); doc.roundedRect(margin, 14, half, 7, 1.5, 1.5, "F"); doc.setFillColor(236, 72, 153); doc.roundedRect(107, 14, half, 7, 1.5, 1.5, "F"); doc.setTextColor(255, 255, 255); doc.setFont(font, "bold"); doc.setFontSize(8.5); doc.text(isThai ? "ยอดขาย Verena vs Commission (บาท)" : "Verena vs Commission (THB)", 58.5, 19, { align: "center" }); doc.text(isThai ? `ROI เทียบกับค่าเฉลี่ย ${roi.toFixed(2)}x` : `ROI vs Average ${roi.toFixed(2)}x`, 151.5, 19, { align: "center" });
  doc.addImage(drawBarChart(staff.map((row, index) => ({ label: row.name, value: row.verena, color: palette[index % palette.length] })), 700, 500), "PNG", margin, 22, half, 68);
  doc.addImage(drawBarChart(staff.map((row, index) => ({ label: row.name, value: row.roi, color: palette[index % palette.length] })), 700, 500, "x"), "PNG", 107, 22, half, 68);
  autoTable(doc, { startY: 96, margin: { left: margin, right: margin }, head: [isThai ? ["พนักงาน", "Sessions", "ยอดขาย Verena", "Commission", "ค่าแรง", "รายได้สุทธิ", "ROI"] : ["Staff", "Sessions", "Verena Sales", "Commission", "Labor cost", "Net Revenue", "ROI"]], body: [...staff.map((row) => [row.name, row.sessions, money(row.verena), money(row.commission), money(row.ot), money(row.revenue), `${row.roi.toFixed(2)}x`]), [isThai ? "รวม" : "TOTAL", sessions.length, money(verena), money(commission), money(ot), money(revenue), `${roi.toFixed(2)}x`]], styles: { fontSize: 8, cellPadding: 2.5, font }, headStyles: { fillColor: [56, 142, 60], textColor: [255, 255, 255], fontStyle: "bold", font }, alternateRowStyles: { fillColor: [240, 255, 244] }, didParseCell: (data) => { if (data.row.index === staff.length) { data.cell.styles.fillColor = [255, 247, 230]; data.cell.styles.fontStyle = "bold"; } } });

  doc.addPage(); header(doc, font, isThai ? "ประสิทธิภาพแยกตามช่วงเวลา" : "Performance by Time Slot", period.title);
  doc.setFillColor(16, 185, 129); doc.roundedRect(margin, 14, half, 7, 1.5, 1.5, "F"); doc.setFillColor(236, 72, 153); doc.roundedRect(107, 14, half, 7, 1.5, 1.5, "F"); doc.setTextColor(255, 255, 255); doc.setFont(font, "bold"); doc.setFontSize(8.5); doc.text(isThai ? "สัดส่วนยอดขายแยกช่วงเวลา" : "Sales Share by Time Slot", 58.5, 19, { align: "center" }); doc.text(isThai ? "ROI แยกช่วงเวลา (x)" : "ROI by Time Slot (x)", 151.5, 19, { align: "center" });
  doc.addImage(drawDonut(early.revenue, late.revenue, beforeLabel, afterLabel), "PNG", margin, 22, half, 68);
  doc.addImage(drawBarChart([{ label: beforeLabel, value: earlyRoi, color: "#10b981" }, { label: afterLabel, value: lateRoi, color: "#ec4899" }], 600, 550, "x"), "PNG", 107, 22, half, 68);
  autoTable(doc, { startY: 96, margin: { left: margin, right: margin }, head: [isThai ? ["ช่วงเวลา", "Sessions", "ค่าแรงรวม", "ยอดขายรวม", "ROI"] : ["Time Slot", "Sessions", "Total labor cost", "Total Sales", "ROI"]], body: [[beforeLabel, before22.length, money(early.ot), money(early.revenue), `${earlyRoi.toFixed(2)}x`], [afterLabel, after22.length, money(late.ot), money(late.revenue), `${lateRoi.toFixed(2)}x`], [isThai ? "รวม" : "TOTAL", sessions.length, money(ot), money(revenue), `${roi.toFixed(2)}x`]], styles: { fontSize: 8, cellPadding: 3, font }, headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: "bold", font }, alternateRowStyles: { fillColor: [240, 255, 250] }, didParseCell: (data) => { if (data.row.index === 2) { data.cell.styles.fillColor = [255, 247, 230]; data.cell.styles.fontStyle = "bold"; } } });

  doc.addPage(); header(doc, font, isThai ? "รายละเอียดทุก Session" : "All Session Details", period.title);
  autoTable(doc, { startY: 12, margin: { left: margin, right: margin, bottom: 12 }, head: [isThai ? ["วันที่", "พนักงาน", "Platform", "เริ่ม", "จบ", "ระยะเวลา", "ค่าแรง", "Verena", "Aff.", "Comm.", "Revenue", "ROI"] : ["Date", "Staff", "Platform", "Start", "End", "Duration", "Labor cost", "Verena", "Aff.", "Comm.", "Revenue", "ROI"]], body: sessions.map((session) => [session.date, session.staffName, session.platformName || "—", localTime(session.startTime), localTime(session.endTime), duration(session.durationMins), money(session.ot), money(session.verenaSales), money(session.affiliateSales), money(session.commission), money(session.totalRevenue), `${session.roi.toFixed(2)}x`]), styles: { fontSize: 7, cellPadding: 1.8, font }, headStyles: { fillColor: [231, 111, 81], textColor: [255, 255, 255], fontStyle: "bold", font }, alternateRowStyles: { fillColor: [252, 245, 235] } });
  const withNotes = sessions.filter((session) => session.startNote || session.endNote);
  if (withNotes.length) {
    let issueY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 12;
    if (issueY > 245) { doc.addPage(); issueY = 12; }
    else issueY += 8;
    doc.setFont(font, "bold"); doc.setTextColor(44, 27, 17); doc.setFontSize(11); doc.text(isThai ? "บันทึกปัญหา" : "Issue Log", margin, issueY);
    autoTable(doc, { startY: issueY + 2, margin: { left: margin, right: margin, bottom: 12 }, head: [isThai ? ["วันที่", "พนักงาน", "ประเภท", "รายละเอียด"] : ["Date", "Staff", "Type", "Issue"]], body: withNotes.flatMap((session) => { const rows: string[][] = []; if (session.startNote) rows.push([session.date, session.staffName, isThai ? "ก่อนไลฟ์" : "Before", session.startNote]); if (session.endNote) rows.push([session.date, session.staffName, isThai ? "หลังไลฟ์" : "After", session.endNote]); return rows; }), styles: { fontSize: 8, cellPadding: 2.5, font }, headStyles: { fillColor: [236, 72, 153], textColor: [255, 255, 255], fontStyle: "bold", font }, alternateRowStyles: { fillColor: [252, 231, 243] } });
  }
  footer(doc, font, input.settings, input.language);
  doc.save(`ROI_${period.fileMonth}_${parsedMonth(input.month).year}_Verena_${isThai ? "TH" : "EN"}.pdf`);
}
